import { FC, useCallback, useState } from "react";
import { FaFilePdf } from "react-icons/fa6";
import { HiChevronDown, HiOutlineTruck } from "react-icons/hi";
import { PiPackage, PiSpinner } from "react-icons/pi";
import { Badge, Button, Text, Title } from "rizzui";
import { toast } from "sonner";
import useFetch from "../../../../hooks/useFetch";
import { PickupService } from "../../../../services/pickup/pickupService";
import { SuggestedProductsApiResponse } from "../../../../type/entities/suggested-product-type";

interface RecommendedProductsSummaryProps {
    headquarters: string[];
}

const RecommendedProductsSummary: FC<RecommendedProductsSummaryProps> = ({
    headquarters
}) => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (data?.data?.length) return data;

        const response = await (new PickupService()).getPickupSuggestedProductsQuantity(headquarters);
        return response.getResponseData() as SuggestedProductsApiResponse;

    }, [headquarters]));

    const handleExportPDF = async () => {
        try {
            if (!headquarters || headquarters.length === 0) {
                toast.error('Debe seleccionar al menos una sede para exportar.');
                return;
            }

            setIsExporting(true);
            const service = new PickupService();
            const response = await service.exportPickupSuggestedProductsQuantity(headquarters);
            const fileData = response.getResponseData();

            if (!fileData) {
                toast.error('No se pudo generar el PDF de productos recomendados');
                return;
            }

            const blob = new Blob([fileData], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `material_recomendado_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success('PDF de productos recomendados descargado con éxito');
        } catch (error: any) {
            toast.error(error?.message || 'Error al exportar PDF de productos recomendados');
        } finally {
            setIsExporting(false);
        }
    };

    // RENDER LOADING STATE
    if (loading) {
        return (
            <div className="bg-white rounded-lg p-4 shadow-sm border">
                <Title as="h2" className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <HiOutlineTruck className="h-5 w-5 text-green-600" />
                    Material Recomendado
                </Title>
                <div className="flex items-center justify-center py-8">
                    <PiSpinner className="h-6 w-6 animate-spin text-gray-400" />
                    <Text className="ml-2 text-gray-500">Cargando productos...</Text>
                </div>
            </div>
        );
    }

    // RENDER EMPTY STATE
    if ((!data || data.length <= 0) && !error) {
        return (
            <div className="bg-white rounded-lg p-4 shadow-sm border">
                <Title as="h2" className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <HiOutlineTruck className="h-5 w-5 text-green-600" />
                    Material Recomendado
                </Title>
                <div className="text-center py-8">
                    <PiPackage className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <Text className="text-gray-500">No hay productos recomendados para hoy</Text>
                </div>
            </div>
        );
    }

    // RENDER ERROR STATE
    if (error && headquarters.length > 0) {
        return (
            <div className="bg-white rounded-lg p-4 shadow-sm border">
                <Title as="h2" className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <HiOutlineTruck className="h-5 w-5 text-green-600" />
                    Material Recomendado
                </Title>
                <div className="text-center py-8">
                    <Text className="text-red-200">
                        Error al cargar productos recomendados
                    </Text>
                </div>
            </div>
        );
    }


    return (
        <div className="bg-white rounded-lg p-4 pe-1 shadow-sm border">
            <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="w-full flex items-center justify-between text-left hover:bg-gray-50 rounded-lg p-2 pe-0 -m-2 transition-colors"
            >
                <Title as="h2" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <HiOutlineTruck className="h-5 w-5 text-green-600" />
                    Material Recomendado ({data.length})
                </Title>
                <HiChevronDown className={`h-5 w-5 text-gray-600 transition-transform duration-300 ease-in-out ${isCollapsed ? 'rotate-0' : 'rotate-180'
                    }`} />
            </button>
            <div
                className={`overflow-hidden transition-all duration-700 ease-in-out ${isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
                    }`}
            >
                <div className="mt-2 pe-3 flex justify-end">
                    <Button
                        size="sm"
                        variant="solid"
                        className="bg-red-600 text-white hover:bg-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleExportPDF}
                        disabled={isExporting || false}
                    >
                        <FaFilePdf className="mr-2 h-4 w-4" />
                        {isExporting ? 'Generando PDF...' : 'Exportar PDF'}
                    </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pe-3">
                    {data.map((product: any) => (
                        <div
                            key={product.productId}
                            className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-green-300 transition-colors"
                        >
                            <div className="flex items-start justify-between mb-2">
                                <Text className="font-medium text-gray-900 text-sm leading-tight">
                                    {product.productName}
                                </Text>
                                <Badge
                                    variant="flat"
                                    color="success"
                                    className="ml-2 flex-shrink-0"
                                >
                                    {product.totalEstimatedQuantity}
                                </Badge>
                            </div>
                            <div className="flex items-center gap-1">
                                <PiPackage className="h-4 w-4 text-gray-400" />
                                <Text className="text-xs text-gray-500">
                                    Cantidad estimada
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default RecommendedProductsSummary;